import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import {
  type GestureResponderEvent,
  View,
  TouchableOpacity,
  type StyleProp,
  type ViewStyle,
  Text,
  StyleSheet,
} from 'react-native'
import type { ComponentProps, ReactElement } from 'react'
import {
  type SegmentAction,
  type SegmentComponent,
  triggerSegmentAction,
} from '@/src/services/segment'
import { mesaColorNames, fontFamilies } from '@/src/common/theme'

interface ChevronWithIconPropsInterface
  extends Pick<ComponentProps<typeof TouchableOpacity>, 'disabled' | 'style'> {
  segmentAction: SegmentAction
  segmentComponent?: SegmentComponent
  Icon?: ReactElement
  IconEnd?: ReactElement
  onIconEndPress?: () => void
  text: string
  textProps?: ComponentProps<typeof Text>
  caption?: string
  onPress?: ((event: GestureResponderEvent) => void) | undefined
  /** @deprecated just use {@link style} */
  containerStyle?: StyleProp<ViewStyle>
  iconContainerStyle?: StyleProp<ViewStyle>
  chevronRightProps?: ComponentProps<typeof ChevronRight>
  testID?: string
}

export function ChevronWithIcon({
  Icon,
  IconEnd,
  onIconEndPress,
  text,
  textProps,
  caption,
  onPress,
  disabled,
  segmentAction,
  segmentComponent = 'Profile Item',
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  containerStyle,
  style,
  iconContainerStyle,
  chevronRightProps,
  testID,
}: ChevronWithIconPropsInterface) {
  return (
    <TouchableOpacity
      testID={testID}
      disabled={disabled}
      style={[chevronStyles.elementContainer, containerStyle, style]}
      onPress={
        onPress
          ? (...args) => {
              void triggerSegmentAction({ action: segmentAction, component: segmentComponent })
              onPress(...args)
            }
          : undefined
      }
    >
      {Icon ? <View style={[chevronStyles.iconView, iconContainerStyle]}>{Icon}</View> : null}
      <View style={chevronStyles.elementInfoContainer}>
        <View style={chevronStyles.elementNameContainer}>
          <Text
            {...textProps}
            style={[
              chevronStyles.elementNameText,
              textProps && 'style' in textProps && textProps.style,
            ]}
          >
            {text}
          </Text>
        </View>
        {caption && (
          <View>
            <Text style={chevronStyles.elementCaption}>{caption}</Text>
          </View>
        )}
      </View>
      <TouchableOpacity
        style={chevronStyles.actionContainer}
        onPress={onIconEndPress}
        testID={`${testID}.IconPressEnd`}
      >
        {IconEnd ?? <ChevronRight {...chevronStyles.chevronRightIcon} {...chevronRightProps} />}
      </TouchableOpacity>
    </TouchableOpacity>
  )
}

export const chevronStyles = StyleSheet.create({
  elementContainer: {
    paddingVertical: 15,
    paddingHorizontal: 10,
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  iconView: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: 40,
    height: 40,
    padding: 8,
    borderWidth: 1,
    borderRadius: 10,
    borderColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
    marginRight: 10,
  },
  icon: { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
  elementInfoContainer: { flex: 1 },
  elementNameContainer: { paddingVertical: 0 },
  elementNameText: {
    fontSize: 14,
    color: mesaColorNames['Greyscale/600 #505D64'],
    fontFamily: fontFamilies['PlusJakartaSans-Bold'],
    fontWeight: 700,
  },
  elementCaption: {
    fontSize: 14,
    color: mesaColorNames['Greyscale/900 #152438'],
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
  },
  actionContainer: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  chevronRightIcon: {
    color: mesaColorNames['Greyscale/500 #6E7375'],
    width: 16,
    height: 16,
  },
})

export default ChevronWithIcon
